<template>
  <div class="warehouse-page">
    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" @click="backMainPanel" class="logo-button" />
    </div>

    <div class="page-content">
      <div class="page-header">
        <div>
          <h2>Vales de Entrada</h2>
          <p class="page-subtitle">Compras que suman existencia al almacén · {{ totalCount }} vales</p>
        </div>
        <div class="header-actions">
          <button v-if="canCreate" class="action-button action-primary" @click="openCreateModal">Nuevo vale de entrada</button>
          <button class="action-button" @click="backWarehouse">Volver a Almacén</button>
        </div>
      </div>

      <div class="filters-card">
        <div class="filter-group">
          <label for="filter-ve-folio">Folio</label>
          <input id="filter-ve-folio" type="text" v-model="filters.voucher_number" @keyup.enter="applyFilters" placeholder="VE-1" />
        </div>
        <div class="filter-group">
          <label for="filter-ve-part">Pieza</label>
          <input id="filter-ve-part" type="text" v-model="filters.part" @keyup.enter="applyFilters" placeholder="Nombre o código" />
        </div>
        <div class="filter-group">
          <label for="filter-ve-auth">Autorizó</label>
          <input id="filter-ve-auth" type="text" v-model="filters.authorized_by" @keyup.enter="applyFilters" />
        </div>
        <div class="filter-group">
          <label for="filter-ve-buyer">Compró</label>
          <input id="filter-ve-buyer" type="text" v-model="filters.purchased_by" @keyup.enter="applyFilters" />
        </div>
        <div class="filter-group">
          <label for="filter-ve-supplier">Proveedor</label>
          <input id="filter-ve-supplier" type="text" v-model="filters.supplier" @keyup.enter="applyFilters" />
        </div>
        <div class="filter-group">
          <label for="filter-ve-from">Desde</label>
          <input id="filter-ve-from" type="date" v-model="filters.start_date" />
        </div>
        <div class="filter-group">
          <label for="filter-ve-to">Hasta</label>
          <input id="filter-ve-to" type="date" v-model="filters.end_date" />
        </div>
        <div class="filter-buttons">
          <button class="action-button action-primary" @click="applyFilters">Buscar</button>
          <button class="action-button" @click="clearFilters">Limpiar</button>
        </div>
      </div>

      <div class="table-card">
        <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th :aria-sort="ariaSort('voucher_number')">
                  <button class="th-sort" @click="toggleSort('voucher_number')">
                    <span>Folio</span>
                    <span class="th-sort__icon" :class="{ 'th-sort__icon--active': sortState.key === 'voucher_number' }">{{ sortIcon('voucher_number') }}</span>
                  </button>
                </th>
                <th :aria-sort="ariaSort('created_at')">
                  <button class="th-sort" @click="toggleSort('created_at')">
                    <span>Fecha</span>
                    <span class="th-sort__icon" :class="{ 'th-sort__icon--active': sortState.key === 'created_at' }">{{ sortIcon('created_at') }}</span>
                  </button>
                </th>
                <th>Orden de compra</th>
                <th>Autorizó</th>
                <th>Compró</th>
                <th>Proveedor</th>
                <th class="col--num">Piezas</th>
                <th class="col--num">Total</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="loading">
                <tr v-for="n in skeletonRows" :key="`skeleton-${n}`">
                  <td v-for="col in 8" :key="col"><span class="skeleton"></span></td>
                </tr>
              </template>
              <template v-else>
                <tr v-for="voucher in vouchers" :key="voucher.id">
                  <td><span class="barcode-pill" role="button" tabindex="0" @click="openDetail(voucher)" @keyup.enter="openDetail(voucher)">{{ voucher.voucher_number }}</span></td>
                  <td>{{ formatDateTime(voucher.created_at) }}</td>
                  <td :class="{ 'cell-muted': !voucher.purchase_order_number }">{{ voucher.purchase_order_number || '—' }}</td>
                  <td :class="{ 'cell-muted': !voucher.authorized_by_name }">{{ voucher.authorized_by_name || '—' }}</td>
                  <td :class="{ 'cell-muted': !voucher.purchased_by_name }">{{ voucher.purchased_by_name || '—' }}</td>
                  <td :class="{ 'cell-muted': !voucher.supplier }">{{ voucher.supplier || '—' }}</td>
                  <td class="col--num">{{ voucher.items.length }}</td>
                  <td class="col--num cell-strong">${{ formatNumber(voucher.total) }}</td>
                </tr>
                <tr v-if="vouchers.length === 0">
                  <td colspan="8" class="empty-row">
                    <div class="empty-state">
                      <span class="empty-state__icon">📥</span>
                      <span class="empty-state__title">No se encontraron vales de entrada</span>
                      <span class="empty-state__hint">Ajusta los filtros o registra una nueva compra.</span>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div class="pagination-container">
          <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1" aria-label="Página anterior">‹</button>
          <span
            v-for="(pageNumber, index) in pageNumbers"
            :key="`${pageNumber}-${index}`"
            :class="{ active: pageNumber === currentPage, ellipsis: pageNumber === ellipsis }"
            @click="goToPage(pageNumber)"
          >
            {{ pageNumber }}
          </span>
          <button @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages" aria-label="Página siguiente">›</button>
          <span class="pagination-summary">{{ totalCount }} resultados</span>
        </div>
      </div>
    </div>

    <!-- Detail modal -->
    <div v-if="detailVoucher" class="modal-overlay" @click.self="detailVoucher = null">
      <div class="modal-card modal-card--wide" role="dialog" aria-modal="true" aria-labelledby="ve-detail-title">
        <h3 id="ve-detail-title">Vale de entrada {{ detailVoucher.voucher_number }}</h3>
        <div class="modal-body">
          <div class="detail-meta">
            <div>
              <span class="detail-meta__label">Fecha</span>
              <span class="detail-meta__value">{{ formatDateTime(detailVoucher.created_at) }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Autorizó</span>
              <span class="detail-meta__value">{{ detailVoucher.authorized_by_name || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Compró</span>
              <span class="detail-meta__value">{{ detailVoucher.purchased_by_name || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Proveedor</span>
              <span class="detail-meta__value">{{ detailVoucher.supplier || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Orden de compra</span>
              <span class="detail-meta__value">{{ detailVoucher.purchase_order_number || 'Sin orden' }}</span>
            </div>
            <div v-if="detailVoucher.comments" class="detail-meta--full">
              <span class="detail-meta__label">Comentarios</span>
              <span class="detail-meta__value">{{ detailVoucher.comments }}</span>
            </div>
          </div>

          <h4 class="section-title">Piezas del vale</h4>
          <table class="data-table data-table--embedded">
            <thead>
              <tr>
                <th>Código</th>
                <th>Pieza</th>
                <th class="col--num">Cantidad</th>
                <th class="col--num">Costo unitario</th>
                <th class="col--num">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in detailVoucher.items" :key="item.id">
                <td>{{ item.part_barcode }}</td>
                <td class="cell-strong">{{ item.part_name }}</td>
                <td class="col--num">{{ formatNumber(item.quantity) }} {{ item.part_unit_of_measure || '' }}</td>
                <td class="col--num">${{ formatNumber(item.unit_cost_snapshot) }}</td>
                <td class="col--num cell-strong">${{ formatNumber(item.subtotal) }}</td>
              </tr>
            </tbody>
          </table>
          <p class="detail-total">Total <strong>${{ formatNumber(detailVoucher.total) }}</strong></p>
        </div>
        <div class="modal-actions">
          <button class="action-button" @click="detailVoucher = null">Cerrar</button>
          <button class="action-button" @click="printTicketCurrent">Imprimir ticket</button>
          <button class="action-button action-primary" @click="printCurrent">Imprimir</button>
        </div>
      </div>
    </div>

    <!-- Create modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="closeCreateModal">
      <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="ve-create-title">
        <h3 id="ve-create-title">Nuevo vale de entrada</h3>
        <div class="modal-body">
          <div class="requisition-fulfill">
            <label for="ve-oc-scan">Recibir orden de compra</label>
            <input
              id="ve-oc-scan"
              type="text"
              v-model="purchaseOrderCode"
              placeholder="Escribir OC-1"
              @keyup.enter="loadPurchaseOrder"
            />
            <button class="mini-button" type="button" @click="loadPurchaseOrder" :disabled="loadingPurchaseOrder">
              {{ loadingPurchaseOrder ? 'Buscando...' : 'Cargar orden' }}
            </button>
          </div>
          <p v-if="form.purchase_order" class="requisition-chip">
            <span>Vinculada a <strong>{{ form.purchase_order_number }}</strong>. Varios vales pueden recibir la misma orden.</span>
            <button type="button" class="mini-button mini-button--danger" @click="clearPurchaseOrder">Quitar</button>
          </p>
          <div class="modal-grid">
            <div class="form-input-group">
              <label for="ve-auth">Autoriza *</label>
              <select id="ve-auth" v-model="form.authorized_by">
                <option value="">Seleccione</option>
                <option v-for="emp in employees" :key="emp.id" :value="emp.id">
                  {{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}
                </option>
              </select>
            </div>
            <div class="form-input-group">
              <label for="ve-buyer">Compró</label>
              <select id="ve-buyer" v-model="form.purchased_by">
                <option value="">Seleccione</option>
                <option v-for="emp in employees" :key="emp.id" :value="emp.id">
                  {{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}
                </option>
              </select>
            </div>
            <div class="form-input-group">
              <label for="ve-supplier">Proveedor</label>
              <input id="ve-supplier" type="text" v-model="form.supplier" />
            </div>
            <div v-if="form.purchase_order" class="form-input-group">
              <label for="ve-received">Recibe la mercancía</label>
              <select id="ve-received" v-model="form.received_by">
                <option value="">Seleccione</option>
                <option v-for="emp in employees" :key="`recv-${emp.id}`" :value="emp.id">
                  {{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}
                </option>
              </select>
            </div>
            <div class="form-input-group form-input-group--full">
              <label for="ve-comments">Comentarios</label>
              <textarea id="ve-comments" v-model="form.comments" rows="2"></textarea>
            </div>
          </div>

          <div class="items-section">
            <div class="items-header">
              <h4>Piezas</h4>
              <input
                type="text"
                class="part-search"
                v-model="partSearch"
                @input="searchParts"
                placeholder="Buscar pieza por nombre o código..."
                aria-label="Buscar pieza"
              />
              <button class="mini-button" @click="addItem">Agregar pieza</button>
            </div>
            <div v-for="(item, index) in form.items" :key="index" class="item-row">
              <select v-model="item.part" aria-label="Pieza">
                <option value="">Seleccione pieza</option>
                <option v-for="part in partOptions" :key="part.id" :value="part.id">
                  {{ part.barcode }} - {{ part.name }} (stock: {{ formatNumber(part.current_stock) }})
                </option>
              </select>
              <input type="number" min="0.01" step="0.01" v-model.number="item.quantity" placeholder="Cantidad" aria-label="Cantidad" />
              <button class="mini-button mini-button--danger" aria-label="Quitar pieza" @click="form.items.splice(index, 1)">✕</button>
            </div>
            <p v-if="form.items.length === 0" class="items-empty">
              Agrega al menos una pieza para guardar el vale.
            </p>
          </div>
        </div>

        <div class="modal-actions">
          <button class="action-button" @click="closeCreateModal">Cancelar</button>
          <button class="action-button action-primary" @click="saveVoucher" :disabled="saving">
            {{ saving ? 'Guardando...' : 'Guardar vale' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from '@/utils/warehouseSwal'
import { printEntryVoucher } from '@/utils/printVoucher'
import { printEntryVoucherTicket } from '@/utils/printTicket'
import { normalizeScannedCode } from '@/utils/scannedCode'
import warehouseTable from '@/mixins/warehouseTable'

export default {
  name: 'WarehouseEntryVouchers',
  mixins: [warehouseTable],
  data() {
    return {
      vouchers: [],
      loading: false,
      saving: false,
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      pageSize: 20,
      filters: {
        voucher_number: '',
        part: '',
        authorized_by: '',
        purchased_by: '',
        supplier: '',
        start_date: '',
        end_date: '',
      },
      sortState: { key: '', dir: 'asc' },
      detailVoucher: null,
      showCreateModal: false,
      form: this.emptyForm(),
      purchaseOrderCode: '',
      loadingPurchaseOrder: false,
      employees: [],
      partOptions: [],
      partSearch: '',
      partSearchTimeout: null,
      permissions: [],
    }
  },
  computed: {
    canCreate() {
      return this.hasPerm('maintenance.create_entry_voucher')
    },
  },
  created() {
    this.loadPermissions()
  },
  mounted() {
    this.fetchVouchers(1)
    this.fetchEmployees()
    this.fetchPartOptions('')
  },
  methods: {
    emptyForm() {
      return {
        authorized_by: '',
        purchased_by: '',
        received_by: '',
        supplier: '',
        comments: '',
        purchase_order: '',
        purchase_order_number: '',
        items: [{ part: '', quantity: null }],
      }
    },
    loadPermissions() {
      try {
        this.permissions = JSON.parse(localStorage.getItem('userPermissions')) || []
      } catch (error) {
        this.permissions = []
      }
    },
    hasPerm(perm) {
      return this.permissions.includes(perm) || this.permissions.includes(`access_control.${perm}`)
    },
    backMainPanel() {
      this.$router.push('/main-panel')
    },
    backWarehouse() {
      this.$router.push('/warehouse')
    },
    formatNumber(value) {
      const num = Number(value)
      if (Number.isNaN(num)) return 'N/A'
      return num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDateTime(value) {
      if (!value) return 'N/A'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return 'N/A'
      return date.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
    },
    toggleSort(key) {
      if (this.sortState.key === key) {
        this.sortState.dir = this.sortState.dir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortState = { key, dir: 'asc' }
      }
      this.fetchVouchers(1)
    },
    applyFilters() {
      this.fetchVouchers(1)
    },
    clearFilters() {
      this.filters = {
        voucher_number: '', part: '', authorized_by: '', purchased_by: '', supplier: '', start_date: '', end_date: '',
      }
      this.fetchVouchers(1)
    },
    goToPage(pageNumber) {
      if (typeof pageNumber !== 'number') return
      if (pageNumber < 1 || pageNumber > this.totalPages || pageNumber === this.currentPage) return
      this.fetchVouchers(pageNumber)
    },
    async fetchVouchers(page) {
      const apiDirectory = process.env.API_DIRECTORY
      this.loading = true
      try {
        let orderingParam = ''
        if (this.sortState.key) {
          orderingParam = `&ordering=${this.sortState.dir === 'desc' ? '-' : ''}${this.sortState.key}`
        }
        const response = await axios.post(
          `${apiDirectory}/warehouse/get-entry-vouchers-filtered?page=${page}&page_size=${this.pageSize}${orderingParam}`,
          this.filters,
          { headers: { 'Content-Type': 'application/json' } },
        )
        this.vouchers = response.data.results || []
        this.totalCount = response.data.count || 0
        this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize))
        this.currentPage = page
      } catch (error) {
        console.error('Failed to fetch entry vouchers:', error)
        Swal.fire('Error', 'No se pudieron cargar los vales de entrada.', 'error')
      } finally {
        this.loading = false
      }
    },
    async fetchEmployees() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const response = await axios.get(`${apiDirectory}/warehouse/employees-for-forms`)
        this.employees = response.data.results || []
      } catch (error) {
        console.error('Failed to fetch employees:', error)
      }
    },
    async fetchPartOptions(search) {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const response = await axios.get(`${apiDirectory}/warehouse/parts-for-forms`, {
          params: { search },
        })
        this.partOptions = response.data.results || []
      } catch (error) {
        console.error('Failed to fetch parts:', error)
      }
    },
    searchParts() {
      clearTimeout(this.partSearchTimeout)
      this.partSearchTimeout = setTimeout(() => {
        this.fetchPartOptions(this.partSearch)
      }, 300)
    },
    openDetail(voucher) {
      this.detailVoucher = voucher
    },
    printCurrent() {
      printEntryVoucher(this.detailVoucher)
    },
    printTicketCurrent() {
      printEntryVoucherTicket(this.detailVoucher)
    },
    openCreateModal() {
      this.form = this.emptyForm()
      this.purchaseOrderCode = ''
      this.showCreateModal = true
    },
    async loadPurchaseOrder() {
      this.purchaseOrderCode = normalizeScannedCode(this.purchaseOrderCode)
      const code = (this.purchaseOrderCode || '').trim()
      if (!code) {
        Swal.fire('Falta el folio', 'Escribe el folio de la orden de compra.', 'warning')
        return
      }
      const apiDirectory = process.env.API_DIRECTORY
      this.loadingPurchaseOrder = true
      try {
        const response = await axios.post(`${apiDirectory}/warehouse/lookup-purchase-order`, { code }, {
          headers: { 'Content-Type': 'application/json' },
        })
        const order = response.data
        if (order.status === 'cancelled') {
          Swal.fire('Orden cancelada', `La orden ${order.order_number} está cancelada.`, 'info')
          return
        }
        if (order.status === 'received') {
          Swal.fire('Orden recibida', `La orden ${order.order_number} ya fue recibida por completo.`, 'info')
          return
        }
        const pending = (order.items || []).filter((item) => Number(item.remaining_quantity) > 0)
        if (pending.length === 0) {
          Swal.fire('Sin pendiente', 'Esta orden no tiene piezas por recibir.', 'info')
          return
        }
        this.partOptions = pending.map((item) => ({
          id: item.part,
          barcode: item.part_barcode,
          name: item.part_name,
          current_stock: item.remaining_quantity,
        }))
        this.form.purchase_order = order.id
        this.form.purchase_order_number = order.order_number
        this.form.supplier = order.provider_name || this.form.supplier
        this.form.items = pending.map((item) => ({
          part: item.part,
          quantity: Number(item.remaining_quantity),
        }))
      } catch (error) {
        console.error('Failed to load purchase order:', error)
        const message = error.response?.data?.error || 'No se encontró la orden de compra.'
        Swal.fire('Error', message, 'error')
      } finally {
        this.loadingPurchaseOrder = false
      }
    },
    clearPurchaseOrder() {
      this.form.purchase_order = ''
      this.form.purchase_order_number = ''
      this.purchaseOrderCode = ''
      this.form.items = [{ part: '', quantity: null }]
      this.fetchPartOptions(this.partSearch)
    },
    closeCreateModal() {
      this.showCreateModal = false
    },
    addItem() {
      this.form.items.push({ part: '', quantity: null })
    },
    async saveVoucher() {
      if (!this.form.authorized_by) {
        Swal.fire('Falta información', 'Selecciona a la persona que autoriza.', 'warning')
        return
      }
      const items = this.form.items.filter((item) => item.part && Number(item.quantity) > 0)
      if (items.length === 0) {
        Swal.fire('Falta información', 'Agrega al menos una pieza con cantidad mayor a cero.', 'warning')
        return
      }
      const apiDirectory = process.env.API_DIRECTORY
      this.saving = true
      try {
        const response = await axios.post(`${apiDirectory}/warehouse/create-entry-voucher`, {
          authorized_by: this.form.authorized_by,
          purchased_by: this.form.purchased_by || null,
          received_by: this.form.received_by || null,
          purchase_order: this.form.purchase_order || null,
          supplier: this.form.supplier || null,
          comments: this.form.comments || null,
          items: items.map((item) => ({ part: item.part, quantity: item.quantity })),
        }, { headers: { 'Content-Type': 'application/json' } })
        this.showCreateModal = false
        const result = await Swal.fire({
          title: 'Vale de entrada creado',
          text: `Folio ${response.data.voucher_number}. ¿Deseas imprimirlo?`,
          icon: 'success',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Imprimir',
          denyButtonText: 'Imprimir ticket',
          cancelButtonText: 'Después',
        })
        if (result.isConfirmed) printEntryVoucher(response.data)
        if (result.isDenied) printEntryVoucherTicket(response.data)
        this.fetchVouchers(1)
        this.fetchPartOptions(this.partSearch)
      } catch (error) {
        console.error('Failed to create entry voucher:', error)
        const message = error.response?.data?.error || 'No se pudo crear el vale de entrada.'
        Swal.fire('Error', message, 'error')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped src="@/styles/warehouse.css"></style>
